import { DataAccess } from "../dataaccess/data.access";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { S25Util } from "../util/s25-util";
import { Proto } from "../pojo/Proto";
import { AccessLevel } from "../pojo/Fls";
import ISODateString = Proto.ISODateString;
import { RecordType } from "../modules/s25-event/ProfileI";
import { EventPerm } from "./avail.service";
import { StandardSchedule } from "./standard.schedule.service";

export class GridsService {
    @Cache({ targetName: "GridsService" })
    public static getList() {
        const url = "/grids/list";
        const injected = DataAccess.injectCaller(url, "GridsService.getGrids");
        return S25Util.Maybe(DataAccess.get<GridsListResponse>(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static delete(id: number) {
        const url = `/grids/grid?id=${id}`;
        const injected = DataAccess.injectCaller(url, "GridsService.delete");
        return S25Util.Maybe(DataAccess.delete(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static create(data: Omit<PutGridListItem, "id">) {
        const url = `/grids/grid`;
        const injected = DataAccess.injectCaller(url, "GridsService.delete");
        return S25Util.Maybe(DataAccess.post<GridListItem>(injected, data));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static update(data: PutGridListItem) {
        const url = `/grids/grid`;
        const injected = DataAccess.injectCaller(url, "GridsService.update");
        return S25Util.Maybe(DataAccess.put<GridListItem>(injected, data));
    }

    public static getData(id: number) {
        const url = `/grids/grid/data.json?id=${id}&version=122`;
        const injected = DataAccess.injectCaller(url, "GridService.getData");
        return S25Util.Result(DataAccess.get<GridDataResponse>(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static setShared(id: number, users: number[]) {
        const url = `/grids/grid/share?id=${id}`;
        const injected = DataAccess.injectCaller(url, "GridService.setShared");
        return S25Util.Result(DataAccess.put<never>(injected, users));
    }

    public static moveItems(gridId: number, changes: GridItemChange[]) {
        const url = `/grids/grid/item/move.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.moveItems");
        return S25Util.Result(DataAccess.post<never>(injected, changes));
    }

    public static getChanges(gridId: number) {
        const url = `/grids/grid/item/changes.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.getChanges");
        return S25Util.Result(DataAccess.get<GridItemChange[]>(injected));
    }

    public static clearChanges(gridId: number) {
        const url = `/grids/grid/item/changes.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.clearChanges");
        return S25Util.Result(DataAccess.delete<void>(injected));
    }

    // TODO: Make sticky?
    public static async getPreferences() {
        return {
            startHour: 7,
            endHour: 17,
            dows: ["MWF", "TR"],
        };
    }
}

export type GridListItem = GridsListResponse[number];

type GridsListResponse = {
    id: number;
    type: "academic" | "schedule-only";
    name: string;
    eventQuery: GridListItemQuery;
    locationQuery?: GridListItemQuery;
    editSchedule: boolean;
    editLocation: boolean;
    owner: number;
    isOwner: boolean;
    sharedWith: { grid: number; contId: number; name: string }[];
    defaultSchedule?: number;
}[];

export type GridListItemQuery = { queryId: number; queryName: string };

export type GridDataResponse = {
    err_msg?: string;
    events: GridEvent[];
    // Other events occupying the grid's rooms during its scheduling window
    spaceConflicts: GridEvent[];
    spaces: Record<number, GridSpace>;
    changes: GridItemChange[];
    resourceAp: Record<number, EventPerm>;
    terms: Record<string, GridDataTerm>;
    defaultSchedule: StandardSchedule[];
};

export type GridEvent = {
    id: number;
    name: string;
    title: string | null;
    locator: Proto.EventReference | null;
    alienUid: string;
    ols: AccessLevel;
    setup: number; // Minutes
    takedown: number; // Minutes
    primaryOrganization?: GridOrganization;
    roles: { id: number; role: number; firstName: string; familyName: string }[];
    attributes: { id: number; value: string }[];
    profiles: GridProfile[];
    type: "EVENT" | "SPACE_CONFLICT";
};

export type GridSpace = {
    id: number;
    name: string;
    defaultInstructionsId: number;
    defaultLayoutId: number;
    maxCapacity: number;
    formalName: string;
    fillRatio: number;
    partition?: { id: number; name: string };
    buildingId: number;
    ols: AccessLevel;
    alwaysShared: boolean;
    ap: EventPerm;
    features: { id: number; name: string }[];
    relationships: {
        blocks: number[];
        blockedBy: number[];
        dividesInto: number[];
        subdivisionOf: number[];
        alsoAssign: number[];
        alsoAssignedBy: number[];
    };
};

export type GridProfile = {
    id: number;
    name: string;
    expectedHeadCount: number;
    registeredHeadCount: number;
    recType: RecordType;
    profileCode: string;
    alienUID: string;
    start: ISODateString;
    end: ISODateString;
    pre: number; // Minutes
    post: number; // Minutes
    boundProfiles: number[];
    featurePreferences: { id: number; name: string }[];
    partitionPreferences: { id: number; name: string }[];
    reservations: GridReservation[];
};

export type GridReservation = {
    id: number;
    start: ISODateString;
    end: ISODateString;
    commentId?: number;
    locations: GridReservationLocation[];
    resources: GridReservationResource[];
};

export type GridReservationLocation = {
    id: number;
    shared: boolean;
    instructionsId?: number;
    layoutId?: number;
    attendance?: number;
};

export type GridReservationResource = {
    id: number;
    quantity: number;
    instructionsId?: number;
};

export type GridItemChange = {
    gridId: number;
    eventId: number;
    profileId: number;
    pattern: string;
    startTime: ISODateString;
    endTime: ISODateString;
};

export type GridDataTerm = {
    cid: number;
    term_code: string;
    term_desc: string;
    start_dt: ISODateString;
    end_dt: ISODateString;
};

export type GridOrganization = {
    id: number;
    name: string;
    ols: AccessLevel;
    partitionPreferences: { id: number; name: string }[][];
};

export type PutGridListItem = Omit<
    GridListItem,
    "eventQuery" | "locationQuery" | "sharedWith" | "owner" | "isOwner"
> & {
    eventQueryId: number;
    locationQueryId?: number;
};
